import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Camera, Quote, Star, CheckCircle2 } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import StatCounter from "@/components/StatCounter";
import ClientLogo from "@/components/ClientLogo";
import { Icon } from "@/lib/icons";
import {
  BRAND, STATS, SERVICES, PROJECTS, TESTIMONIALS, CLIENTS, INDUSTRIES, WHY_CHOOSE, TRUST_BADGES, img,
} from "@/config";

/* ------------------------------- HERO ------------------------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden pt-32">
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
        <video
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={img("hero_cctv")}
          className="h-full w-full object-cover opacity-100"
        >
          <source src="/hero-security.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1120]/60 via-[#0B1120]/75 to-[#0B1120]" />
        <div className="tech-grid absolute inset-0 opacity-50" />
      </motion.div>
      {/* live surveillance indicator */}
      <div className="absolute right-6 top-28 z-10 hidden items-center gap-2 rounded-full border border-slate-700 bg-[#0B1120]/60 px-3 py-1.5 backdrop-blur-sm lg:flex">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
        </span>
        <span className="text-xs font-medium uppercase tracking-widest text-slate-300">Live Monitoring</span>
      </div>
      <div className="glow-blue pointer-events-none absolute -left-32 top-20 h-[520px] w-[520px]" />

      <motion.div style={{ opacity }} className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#60A5FA] backdrop-blur-sm">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#2563EB]" />
            {BRAND.tagline}
          </span>
        </motion.div>

        <MaskedLines
          className="mt-8 font-display font-extrabold tracking-tight text-white"
          lineClassName="text-4xl leading-[1.03] sm:text-6xl lg:text-7xl"
          lines={["Securing India's", <span key="l2">Digital &amp; Physical</span>, <span key="l3"><span className="text-[#2563EB]">Infrastructure</span>.</span>]}
        />

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-300">
          CCTV surveillance, enterprise networking, access control and complete IT
          infrastructure — trusted by government and corporate clients across India,
          with 900+ successful installations.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/contact" data-testid="hero-survey-cta" className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[#3b82f6] hover:shadow-[0_8px_40px_rgba(37,99,235,0.5)]">
            Get a Free Site Survey
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/projects" data-testid="hero-projects-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-white/5">
            View Our Projects <ArrowRight className="h-5 w-5" />
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="mt-10 flex flex-wrap gap-x-6 gap-y-2">
          {TRUST_BADGES.map((b) => (
            <span key={b} className="inline-flex items-center gap-1.5 text-sm text-slate-400">
              <CheckCircle2 className="h-4 w-4 text-[#2563EB]" /> {b}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}

/* -------------------------------- STATS -------------------------------- */
function Stats() {
  return (
    <section className="border-y border-slate-800 bg-[#0B1120]" data-testid="stats-section">
      <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-slate-800 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div className="px-6 py-10 text-center lg:py-12">
              <p className="font-display text-4xl font-extrabold tracking-tight text-white lg:text-5xl">
                <StatCounter value={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-2 text-sm text-slate-400">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------ CLIENTS ------------------------------ */
function Clients() {
  return (
    <section className="py-14" data-testid="clients-section">
      <p className="mb-8 text-center text-xs font-medium uppercase tracking-[0.3em] text-slate-500">
        Trusted by government &amp; corporate leaders
      </p>
      <Marquee gradient gradientColor="#0B1120" gradientWidth={120} speed={40} pauseOnHover>
        {CLIENTS.concat(CLIENTS).map((c, i) => (
          <ClientLogo key={i} name={c.name} short={c.short} />
        ))}
      </Marquee>
    </section>
  );
}

/* ------------------------------ SERVICES ------------------------------ */
function ServicesSnapshot() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="services-overview">
      <div className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div>
          <Reveal><span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#60A5FA]">What we do</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white lg:text-5xl">End-to-end security &amp; IT infrastructure</h2></Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link to="/services" className="group inline-flex items-center gap-2 text-sm font-semibold text-white">All services <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link>
        </Reveal>
      </div>

      <div className="grid gap-px overflow-hidden rounded-3xl border border-slate-800 bg-slate-800 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((service, i) => (
          <Reveal key={service.slug} delay={(i % 4) * 0.06}>
            <Link
              to={`/services#${service.slug}`}
              data-testid={`service-card-${service.slug}`}
              className="group flex h-full flex-col bg-[#0B1120] p-8 transition-colors hover:bg-[#111c33]"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-800 bg-white/5 text-[#60A5FA] transition-colors group-hover:bg-[#2563EB] group-hover:text-white">
                <Icon name={service.icon} className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-white">{service.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{service.short}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#60A5FA]">Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* --------------------------- FEATURED PROJECTS --------------------------- */
function FeaturedProjects() {
  const featured = PROJECTS.slice(0, 4);
  return (
    <section className="relative overflow-hidden py-24" data-testid="featured-projects">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal><span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#60A5FA]">Proven track record</span></Reveal>
        <Reveal delay={0.1}><h2 className="mt-4 max-w-2xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white lg:text-5xl">Projects that secure India</h2></Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link to="/projects" data-testid={`featured-project-${p.slug}`} className="group relative block overflow-hidden rounded-3xl border border-slate-800">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={img(p.image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-[#2563EB] px-3 py-1 text-xs font-semibold text-white">{p.category}</span>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#60A5FA]"><Camera className="h-4 w-4" /> {p.metric}</span>
                  </div>
                  <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-white lg:text-2xl">{p.title}</h3>
                  <p className="mt-1 text-sm text-slate-300">{p.scope}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 text-center">
            <Link to="/projects" className="group inline-flex items-center gap-2 rounded-full border border-slate-700 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/5">
              View all projects <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ----------------------------- WHY CHOOSE ----------------------------- */
function WhyChoose() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="why-choose-section">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal><span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#60A5FA]">Why choose us</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-4 font-display text-4xl font-extrabold leading-tight tracking-tight text-white lg:text-5xl">Government-grade reliability, end to end.</h2></Reveal>
          <Reveal delay={0.2}><p className="mt-5 text-lg leading-relaxed text-slate-400">From the first site survey to lifelong support, we deliver security infrastructure that simply works — at any scale.</p></Reveal>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:col-span-8">
          {WHY_CHOOSE.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-800 bg-[#111c33]/40 p-7 transition-colors hover:border-slate-700">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#60A5FA]"><Icon name={w.icon} className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-lg font-bold text-white">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- INDUSTRIES ---------------------------- */
function IndustriesStrip() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10" data-testid="industries-strip">
      <Reveal><h2 className="font-display text-3xl font-extrabold tracking-tight text-white lg:text-4xl">Industries we serve</h2></Reveal>
      <div className="mt-10 flex flex-wrap gap-3">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.title} delay={i * 0.05}>
            <Link to="/industries" className="group inline-flex items-center gap-2.5 rounded-full border border-slate-800 bg-[#111c33]/40 px-5 py-3 transition-colors hover:border-[#2563EB]">
              <Icon name={ind.icon} className="h-5 w-5 text-[#60A5FA]" />
              <span className="text-sm font-medium text-white">{ind.title}</span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ----------------------------- TESTIMONIALS ----------------------------- */
function Testimonials() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10" data-testid="testimonials-section">
      <Reveal><h2 className="max-w-3xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white lg:text-5xl">What our clients say</h2></Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <figure className="flex h-full flex-col justify-between rounded-3xl border border-slate-800 bg-[#111c33]/40 p-8">
              <div>
                <div className="flex gap-1 text-[#2563EB]">{[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4" fill="currentColor" strokeWidth={0} />)}</div>
                <Quote className="mt-5 h-8 w-8 text-[#2563EB]/40" />
                <blockquote className="mt-4 text-lg leading-relaxed text-slate-200">{t.quote}</blockquote>
              </div>
              <figcaption className="mt-8 border-t border-slate-800 pt-5">
                <p className="font-display font-bold text-white">{t.name}</p>
                <p className="text-sm text-slate-400">{t.role}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------- CTA -------------------------------- */
function CTA() {
  return (
    <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-[#111c33] px-8 py-16 lg:px-16 lg:py-20">
          <div className="tech-grid absolute inset-0 opacity-40" />
          <div className="glow-blue pointer-events-none absolute left-1/2 top-0 h-[400px] w-[600px] -translate-x-1/2 -translate-y-1/2" />
          <div className="relative grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white lg:text-5xl">Let's secure your infrastructure.</h2>
              <p className="mt-5 max-w-lg text-lg text-slate-300">Book a free site survey. Our engineers assess your premises and design a tailored security solution — no obligation.</p>
            </div>
            <div className="flex flex-wrap gap-4 lg:justify-end">
              <Link to="/contact" data-testid="cta-survey-button" className="group inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-[#3b82f6]">
                Get a Free Site Survey <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a href={`tel:${BRAND.phoneRaw}`} data-testid="cta-call-button" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-white/5">Call {BRAND.phone}</a>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Stats />
      <Clients />
      <ServicesSnapshot />
      <FeaturedProjects />
      <WhyChoose />
      <IndustriesStrip />
      <Testimonials />
      <CTA />
    </div>
  );
}
